import React, {useEffect, useState} from "react";
import {Box, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col} from "reactstrap";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import UIUtils from "../../../utils";
import useIndustry from "../../../hooks/use-industry";
import PartAutocomplete from "../../text-fields/autocomplete/part-autocomplete";

const ProductSelectionDialog = ({open, close, existingProductIds, onSelect, industry}) => {
    const {industries} = useIndustry({noCategories: true})
    const [loading, setLoading] = useState(false);
    const [selectedPart, setSelectedPart] = useState(null);
    const isMounted = useIsMounted();

    /**
     * With each change in the open state of the dialog:
     * - if the dialog is closed, then removes the value of the selected part state.
     */
    useEffect(() => {
        if (!open) {
            setSelectedPart(null);
        }
    }, [open])

    /**
     * Invokes the onSelect callback of the dialog with the newly selected product.
     *
     * * if the resultFlag of the callback returns true, closes the dialog as well.
     * @param {any} product
     * @return {Promise<void>}
     */
    const onNewPartSelected = async (product) => {
        setSelectedPart(product);
        setLoading(true);
        const resultFlag = await onSelect(product);
        if (!isMounted())
            return;
        setLoading(false);
        if (resultFlag) {
            close();
        }
    }

    /**
     * Filters the autocomplete options to only include the products that do not exist in the relation.
     * @param options
     * @return {unknown[]}
     */
    const filterAutocompleteOptions = (options) => {
        return options.filter(e => !existingProductIds.includes(e.value.id));
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'sm'}
                loading={loading}
                disableEscapeKeyDown={loading}
                disableBackdropClick={loading}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Connect a Part from {industry?.title ?? ""} Industry
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2'}>
                    <Col xs={12} className={'mb-4 mt-4'}>
                        <p className={'text-sm font-weight-regular mt-2 mb-2'}>
                            Search over parts with part numbers that exist in the ${industry?.title ?? ""} industry and select from suggested list
                        </p>
                    </Col>
                    <Col xs={12} className={'mb-5'}>
                        <Box sx={{display: 'flex', alignItems: 'center',}}>
                            {
                                industries.length > 1 &&
                                <Box sx={{
                                    mr: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: 'background.default',
                                    color: 'primary.main'
                                }}>
                                    {
                                        React.cloneElement(UIUtils.getIndustryIcon(industry?.iconFileName, industry?.id, true) ??
                                            <div/>, {
                                            style: {width: 24, height: 24, color: 'inherit'},
                                        })
                                    }
                                </Box>
                            }
                            <Box sx={{flexGrow: 1}}>
                                <PartAutocomplete
                                    clearOnBlur
                                    fullWidth
                                    disabled={loading}
                                    label={"New Product"}
                                    value={selectedPart}
                                    onChange={(_, value) => value && onNewPartSelected(value)}
                                    filterOptions={(options) => options.filter(e => !existingProductIds.includes(e.id))}
                                />
                            </Box>
                        </Box>
                    </Col>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default ProductSelectionDialog;
